import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type {
  GetIndicatorByIdQuery,
  GetIndicatorResultsByIndicatorIdQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { Indicator_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { MutableRefObject } from 'react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { IndicatorThresholds } from 'src/pages/indicators/calculateConformanceRating';
import {
  calculatePercentageDifference,
  conformanceIndicatorRating,
  getConformanceTrendRating,
  percentageDifferenceValue,
} from 'src/pages/indicators/calculateConformanceRating';
import type {
  ConformanceIndicatorRating,
  ConformanceTrend,
} from 'src/pages/indicators/types';
import { Permission } from 'src/rbac/Permission';
import type { UseGetTablePropsOptions } from 'src/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from 'src/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from 'src/utils/table/types';

import Link from '@/components/link';
import { toLocalDate } from '@/utils/dateUtils';
import { compareNullableNumber } from '@/utils/numberUtils';
import { useSortingDirectionTracker } from '@/utils/table/hooks/useSortingDirectionTracker';

export type IndicatorResultFields =
  GetIndicatorResultsByIndicatorIdQuery['indicator_result'][number] & {
    previous?: GetIndicatorResultsByIndicatorIdQuery['indicator_result'][number];
  };

/**
 * Minimal shape the Result column's display + sort comparator need — kept
 * structural (not a `Pick` of the generated row type) so callers and tests can
 * pass lightweight objects, matching the existing `getResult` convention.
 */
type ResultSortFields = {
  TargetValueNum?: null | number;
  TargetValueTxt?: null | string;
  parent?: { Type: Indicator_Type_Enum } | null;
};

type IndicatorTableFields = IndicatorResultFields & {
  ModifiedByUserName: string;
  Conformance: ConformanceIndicatorRating;
  ConformanceTrend: ConformanceTrend | null;
  PercentageChange: string;
  Result: string;
};

const useLabelledFields = (
  indicator: GetIndicatorByIdQuery['indicator'][number],
  data: GetIndicatorResultsByIndicatorIdQuery | undefined
) => {
  return useMemo<IndicatorTableFields[]>(() => {
    const labelledFields =
      data?.indicator_result.map((r, i) => ({
        ...r,
        previous: data.indicator_result[i - 1],
      })) || [];

    return labelledFields.map((item) => ({
      ...item,
      ModifiedByUserName: item.modifiedBy?.FriendlyName || '-',
      Description: item.Description || '-',
      Conformance: conformanceIndicatorRating(indicator, item),
      PercentageChange: getPercentageChange(item),
      ConformanceTrend: getConformanceTrendRatingValue(indicator, item),
      Result: getResult(item),
    }));
  }, [data, indicator]);
};

const useGetFieldConfig = (
  onEdit: (indicator: IndicatorTableFields) => void,
  // Mutable ref kept in sync with the table's live sort direction so the
  // Result / Percentage change comparators pin sentinel rows (no value / "-")
  // to the bottom in both directions. See `useSortingDirectionTracker`.
  sortingDescendingRef: MutableRefObject<boolean>
): TableFields<IndicatorTableFields> => {
  const { getByValue: statusGetByValue } = useRating(
    'indicator_conformance_status'
  );
  const { getByValue: trendGetByValue } = useRating(
    'indicator_conformance_trend'
  );
  const { t } = useTranslation(['common'], {
    keyPrefix: 'indicator_results.columns',
  });

  return useMemo<TableFields<IndicatorTableFields>>(
    () => ({
      ResultDate: {
        formId: 'indicator_result',
        fieldId: 'ResultDate',
        cell: (item) => (
          <Link variant={'secondary'} href={'#'} onFollow={() => onEdit(item)}>
            {toLocalDate(item.ResultDate)}
          </Link>
        ),
        sortingComparator: compareByResultDateTime,
        isRowHeader: true,
      },
      Description: {
        formId: 'indicator_result',
        fieldId: 'Description',
      },
      ModifiedByUserName: {
        header: t('modified_by'),
      },
      Result: {
        header: t('result'),
        sortingComparator: (a, b) =>
          compareByResult(a, b, sortingDescendingRef.current),
      },
      Conformance: {
        id: 'conformance',
        header: t('conformance'),
        cell: (item) => (
          <SimpleRatingBadge rating={statusGetByValue(item.Conformance)} />
        ),
      },
      ConformanceTrend: {
        header: t('conformance_trend'),
        cell: (item) => {
          const rating = trendGetByValue(item.ConformanceTrend);

          return <SimpleRatingBadge rating={rating} />;
        },
      },
      PercentageChange: {
        header: t('percentage_change'),
        sortingComparator: (a, b) =>
          compareNullableNumber(
            getPercentageChangeValue(a),
            getPercentageChangeValue(b),
            sortingDescendingRef.current
          ),
        fieldType: 'number',
      },
    }),
    [statusGetByValue, trendGetByValue, onEdit, t, sortingDescendingRef]
  );
};

/**
 * Sort comparator for the Result column. Number-type indicators sort by the
 * numeric `TargetValueNum` (so 100 > 30 > 5, not the lexicographic "100" < "30"
 * < "5"); other indicator types fall back to a locale-aware compare of the
 * displayed value. Number rows with no value pin to the bottom in both
 * directions.
 */
export const compareByResult = (
  a: ResultSortFields,
  b: ResultSortFields,
  isDescending: boolean
): number => {
  const isNumberIndicator =
    a.parent?.Type === Indicator_Type_Enum.Number ||
    b.parent?.Type === Indicator_Type_Enum.Number;

  if (isNumberIndicator) {
    return compareNullableNumber(
      a.TargetValueNum ?? null,
      b.TargetValueNum ?? null,
      isDescending
    );
  }

  return getResult(a).localeCompare(getResult(b));
};

/**
 * Sorts by the full ResultDate datetime, falling back to CreatedAtTimestamp so
 * results sharing the same ResultDate (e.g. date-only entries stored at
 * midnight) are ordered by when they were recorded.
 */
export const compareByResultDateTime = (
  a: Pick<IndicatorResultFields, 'ResultDate' | 'CreatedAtTimestamp'>,
  b: Pick<IndicatorResultFields, 'ResultDate' | 'CreatedAtTimestamp'>
): number => {
  const resultDateDiff =
    new Date(a.ResultDate).getTime() - new Date(b.ResultDate).getTime();
  if (resultDateDiff !== 0) {
    return resultDateDiff;
  }

  return (
    new Date(a.CreatedAtTimestamp).getTime() -
    new Date(b.CreatedAtTimestamp).getTime()
  );
};

const getConformanceTrendRatingValue = (
  indicator: IndicatorThresholds,
  item: IndicatorResultFields
) => {
  const results = [item];
  if (item.previous) {
    results.push(item.previous);
  }

  return getConformanceTrendRating(indicator, results);
};

const getPercentageChange = (item: IndicatorResultFields) => {
  if (item.previous?.TargetValueNum == null || item.TargetValueNum == null) {
    return '-';
  }

  return calculatePercentageDifference(
    item.TargetValueNum,
    item.previous.TargetValueNum
  );
};

/**
 * Numeric value backing the Percentage change column's sort. Mirrors
 * {@link getPercentageChange} but returns the signed number (or `null` for the
 * "-" cases: no previous result, or a zero previous value) so the column sorts
 * numerically rather than lexicographically on the formatted "x.xx%" string.
 */
export const getPercentageChangeValue = (item: {
  TargetValueNum?: null | number;
  previous?: { TargetValueNum?: null | number } | null;
}): number | null => {
  if (item.previous?.TargetValueNum == null || item.TargetValueNum == null) {
    return null;
  }

  return percentageDifferenceValue(
    item.TargetValueNum,
    item.previous.TargetValueNum
  );
};

export const getResult = (item: ResultSortFields): string => {
  let result = '-';
  if (
    item.parent?.Type === Indicator_Type_Enum.Number &&
    !_.isNil(item.TargetValueNum)
  ) {
    result = `${item.TargetValueNum}`;
  }
  if (item.parent?.Type === Indicator_Type_Enum.Text && item.TargetValueTxt) {
    result = item.TargetValueTxt;
  }

  return result;
};

const useGetIndicatorTableProps = (
  indicator: GetIndicatorByIdQuery['indicator'][number],
  data: GetIndicatorResultsByIndicatorIdQuery | undefined,
  onEdit: (indicator: IndicatorTableFields) => void,
  handleModalOpen: () => void,
  sortingDescendingRef: MutableRefObject<boolean>
): UseGetTablePropsOptions<IndicatorTableFields> => {
  const labelledFields = useLabelledFields(indicator, data);
  const { t } = useTranslation(['common'], { keyPrefix: 'indicator_results' });
  const fields = useGetFieldConfig(onEdit, sortingDescendingRef);

  return {
    tableId: 'indicatorTab',
    data: labelledFields,
    customAttributeFormIds: ['indicator_result'],
    entityLabel: t('entity_name'),
    emptyCollectionAction: (
      <Permission
        permission={'insert:indicator_result'}
        parentObject={indicator}
      >
        <Button formAction={'none'} onClick={handleModalOpen}>
          {t('create_new_button')}
        </Button>
      </Permission>
    ),
    preferencesStorageKey: 'IndicatorTabTable-Preferences',
    enableFiltering: false,
    initialColumns: [
      'ResultDate',
      'Description',
      'ModifiedByUserName',
      'Result',
      'Conformance',
      'ConformanceTrend',
      'PercentageChange',
    ],
    fields,
    defaultSortingState: {
      sortingColumn: 'ResultDate',
      sortingDirection: 'desc',
    },
  };
};

export const useGetCollectionTableProps = (
  indicator: GetIndicatorByIdQuery['indicator'][number],
  data: GetIndicatorResultsByIndicatorIdQuery | undefined,
  onEdit: (indicator: IndicatorTableFields) => void,
  handleModalOpen: () => void
): TablePropsWithActions<IndicatorTableFields> => {
  const sortingDescendingRef = useRef(false);
  const props = useGetIndicatorTableProps(
    indicator,
    data,
    onEdit,
    handleModalOpen,
    sortingDescendingRef
  );
  const tableProps = useGetTableProps(props);

  return useSortingDirectionTracker(tableProps, sortingDescendingRef);
};
